'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterPanelProps {
  categories: FilterOption[];
  types: FilterOption[];
  materials: FilterOption[];
  processes: FilterOption[];
  onFilterChange: (filters: {
    categories: string[];
    types: string[];
    materials: string[];
    processes: string[];
  }) => void;
}

export default function FilterPanel({
  categories,
  types,
  materials,
  processes,
  onFilterChange,
}: FilterPanelProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>(['categories', 'types']);

  useState(() => {
    setIsHydrated(true);
  });

  const toggleSection = (section: string) => {
    if (!isHydrated) return;
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const handleCategoryChange = (categoryId: string) => {
    if (!isHydrated) return;
    const updated = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(updated);
    onFilterChange({
      categories: updated,
      types: selectedTypes,
      materials: selectedMaterials,
      processes: selectedProcesses,
    });
  };

  const handleTypeChange = (typeId: string) => {
    if (!isHydrated) return;
    const updated = selectedTypes.includes(typeId)
      ? selectedTypes.filter((id) => id !== typeId)
      : [...selectedTypes, typeId];
    setSelectedTypes(updated);
    onFilterChange({
      categories: selectedCategories,
      types: updated,
      materials: selectedMaterials,
      processes: selectedProcesses,
    });
  };

  const handleMaterialChange = (materialId: string) => {
    if (!isHydrated) return;
    const updated = selectedMaterials.includes(materialId)
      ? selectedMaterials.filter((id) => id !== materialId)
      : [...selectedMaterials, materialId];
    setSelectedMaterials(updated);
    onFilterChange({
      categories: selectedCategories,
      types: selectedTypes,
      materials: updated,
      processes: selectedProcesses,
    });
  };

  const handleProcessChange = (processId: string) => {
    if (!isHydrated) return;
    const updated = selectedProcesses.includes(processId)
      ? selectedProcesses.filter((id) => id !== processId)
      : [...selectedProcesses, processId];
    setSelectedProcesses(updated);
    onFilterChange({
      categories: selectedCategories,
      types: selectedTypes,
      materials: selectedMaterials,
      processes: updated,
    });
  };

  const clearAllFilters = () => {
    if (!isHydrated) return;
    setSelectedCategories([]);
    setSelectedTypes([]);
    setSelectedMaterials([]);
    setSelectedProcesses([]);
    onFilterChange({
      categories: [],
      types: [],
      materials: [],
      processes: [],
    });
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="h-6 bg-muted rounded w-1/2 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const FilterSection = ({
    title,
    sectionId,
    options,
    selectedValues,
    onChange,
  }: {
    title: string;
    sectionId: string;
    options: FilterOption[];
    selectedValues: string[];
    onChange: (id: string) => void;
  }) => {
    const isExpanded = expandedSections.includes(sectionId);

    return (
      <div className="border-b border-border pb-4">
        <button
          onClick={() => toggleSection(sectionId)}
          className="w-full flex items-center justify-between mb-3 group"
        >
          <span className="font-headline text-sm font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
            {title}
          </span>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className={`text-technical-gray transform transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isExpanded && (
          <div className="space-y-2">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex items-center space-x-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.id)}
                  onChange={() => onChange(option.id)}
                  className="w-4 h-4 rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                />
                <span className="flex-1 font-body text-sm text-technical-gray group-hover:text-foreground transition-colors duration-300">
                  {option.label}
                </span>
                <span className="font-body text-xs text-technical-gray">
                  ({option.count})
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const activeFilterCount =
    selectedCategories.length +
    selectedTypes.length +
    selectedMaterials.length +
    selectedProcesses.length;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-headline text-lg font-semibold text-foreground">
          Filters
        </h3>
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="flex items-center space-x-1 text-accent hover:text-accent/80 transition-colors duration-300"
          >
            <span className="font-cta text-xs">CLEAR ALL</span>
            <span className="px-2 py-0.5 bg-accent/20 text-accent font-cta text-xs rounded">
              {activeFilterCount}
            </span>
          </button>
        )}
      </div>

      <div className="space-y-4">
        <FilterSection
          title="Categories"
          sectionId="categories"
          options={categories}
          selectedValues={selectedCategories}
          onChange={handleCategoryChange}
        />

        <FilterSection
          title="Resource Type"
          sectionId="types"
          options={types}
          selectedValues={selectedTypes}
          onChange={handleTypeChange}
        />

        <FilterSection
          title="Materials"
          sectionId="materials"
          options={materials}
          selectedValues={selectedMaterials}
          onChange={handleMaterialChange}
        />

        <FilterSection
          title="Processes"
          sectionId="processes"
          options={processes}
          selectedValues={selectedProcesses}
          onChange={handleProcessChange}
        />
      </div>
    </div>
  );
}